import React, { useRef, useState, memo, useEffect, CSSProperties } from "react";
import { SyncService } from "frontend/services/syncService";
import Draggable from "react-draggable";
import usePermissionsApi from "frontend/hooks/use-permissions-api";
import Modal from "frontend/modal/modal";
import dynamic from "next/dynamic";
import { useBoardValue } from "frontend/state/board-state";
import { ConnectionStatus, RemoteStreamData, VideoRoomHandler } from "./video-room-handler";
import StyledButton from "frontend/ui-components/styled-button";
import style from "./video-call-ui.module.css";
import { CloseIcon } from "frontend/ui-components/svg-shapes";
import cn from "classnames";
import { BoardUser } from "shared/datamodel/schemas";
import { useCollaboratorsInVideoChat } from "frontend/subscriptions";
import { useAsync, useUpdate } from "react-use";
import DropdownPicker from "frontend/ui-components/picker/dropdown-picker";
import useUserMedia from "frontend/hooks/use-user-media";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import { Box, Row, Stack, AvoidControls } from "./video-ui-components";
import { any, groupBy, prop } from "rambda";
import { minIndexBy } from "frontend/utils/fn-utils";
import { getPathPrefix } from "../../utils/getPathPrefix";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
const Player = dynamic(() => import("@lottiefiles/react-lottie-player").then((mod) => mod.Player), {
  ssr: false,
});

const defaultConstraints: MediaStreamConstraints = {
  audio: {
    echoCancellation: { ideal: true },
  },
  video: {
    facingMode: "user",
    frameRate: { min: 15, ideal: 30, max: 60 },
    width: { ideal: 224 },
    height: { ideal: 128 },
    // resizeMode: "none", // without this the browser crops and scale the image depending on aspect-ratio of container
  },
};

const cameraPriority = (cam: { label: string }) => {
  if (/facetime/i.test(cam.label)) return 1;
  if (/iphone camera/i.test(cam.label)) return 10;
  return 100;
};

const getBestCamera = minIndexBy(cameraPriority);
const anyNotEnabled = any((t: { enabled: boolean }) => !t.enabled);

export const VideoCallUi = memo(
  ({
    syncService,
    onFail,
    onClose,
  }: {
    syncService: SyncService<RW> | null;
    onFail: (reason: string) => void;
    onClose: () => void;
  }) => {
    const [constraints, setConstraints] = useState<null | MediaStreamConstraints>(null);
    const { stream, error } = useUserMedia(constraints);
    const [{ documentId, user }] = useBoardValue();
    const [status, setStatus] = useState<ConnectionStatus>("idle");
    const roomRef = useRef<any>(null);

    if (roomRef.current == null) {
      roomRef.current = new VideoRoomHandler(documentId, setStatus);
    }

    function trackEvent(action: string) {
      tracking.trackEvent(consts.TRACKING_CATEGORY.VIDEO_CALLS, action, documentId, user?.id);
    }

    useEffect(() => {
      function close() {
        trackEvent("video_call_ui_close");
        syncService?.getReplicache()?.mutate.setVideoPresence(null);
        roomRef.current?.stop();
      }
      window.addEventListener("unload", close);
      trackEvent("video_call_ui_open");
      return close;
    }, []);

    if (error) {
      return <VideoMessageModal onDismiss={onClose}>{error}</VideoMessageModal>;
    }

    async function tryJoin(stream: MediaStream): Promise<"connected" | "in-progress" | "canceled"> {
      const room = roomRef.current;
      const sessionData = await room.connect(stream);
      if (sessionData == "canceled" || sessionData == "in-progress") {
        return sessionData;
      }
      const videoMuted = anyNotEnabled(stream.getVideoTracks());
      const audioMuted = anyNotEnabled(stream.getAudioTracks());
      await syncService?.getReplicache()?.mutate.setVideoPresence({
        sessionId: sessionData.sessionId,
        videoTrackName: sessionData.videoTrackName,
        audioTrackName: sessionData.audioTrackName,
        audioMuted,
        videoMuted,
      });
      return "connected";
    }

    async function joinCall(stream: MediaStream) {
      let room = roomRef.current;

      if (room.status != "idle" && room.status != "failed") {
        return; // we're connected already or connecting
      }

      let tries = 0;
      while (tries < 3) {
        try {
          await tryJoin(stream);
          trackEvent("video_call_connect");
          return;
        } catch (error) {
          tries++;
          if (tries == 3) {
            if (error instanceof Error) {
              console.error("Error", error.name, error.message, error.cause, error.stack);
            } else {
              console.error(error);
            }
            onFail("Error connecting to video call");
            onClose();
          }
          await new Promise((resolve) => setTimeout(resolve, 500));
          // create a new room handler for a fresh try (best way to reset everything)
          roomRef.current = new VideoRoomHandler(documentId, setStatus);
        }
      }
    }

    if (stream && status == "connected") {
      return (
        <VideoCallViews
          syncService={syncService}
          user={user}
          stream={stream}
          room={roomRef.current}
          onHangup={onClose}
        />
      );
    }
    return (
      <VideoCallWaitingRoom
        syncService={syncService}
        stream={stream}
        setConstraints={setConstraints}
        constraints={constraints}
        userInfo={user}
        onDismiss={onClose}
        onConnectToCall={joinCall}
        status={roomRef.current.status}
      />
    );
  }
);

VideoCallUi.displayName = "VideoCallUi";

const VideoMessageModal: React.FC<{ onDismiss: () => void }> = ({ onDismiss, children }) => (
  <Modal>
    <AvoidControls>
      <VideoModal>
        <Box padding="25px">{children}</Box>
        <div role="button" className={style.dismissButton} onClick={onDismiss}>
          <CloseIcon color="currentColor" />
        </div>
      </VideoModal>
    </AvoidControls>
  </Modal>
);

function VideoCallWaitingRoom({
  syncService,
  stream,
  setConstraints,
  constraints,
  userInfo,
  onConnectToCall,
  onDismiss,
  status,
}: {
  syncService: SyncService<RW> | null;
  stream: MediaStream | null;
  setConstraints: React.Dispatch<React.SetStateAction<MediaStreamConstraints | null>>;
  constraints: MediaStreamConstraints | null;
  userInfo?: BoardUser | null;
  onConnectToCall: (stream: MediaStream) => void;
  onDismiss: () => void;
  status: ConnectionStatus;
}) {
  // TODO: this gets called on every mouse-move of every collaborator
  const videoChat = useCollaboratorsInVideoChat(syncService?.getReplicache());
  const devicesOrFail = useAsync(() => navigator.mediaDevices.enumerateDevices(), []);
  const permissions = usePermissionsApi(["camera", "microphone"]);

  const [settingsPanel, setSettingsPanel] = useState(false);
  const refresh = useUpdate();

  useEffect(() => {
    if (devicesOrFail.loading) {
      return;
    }

    if (devicesOrFail.error) {
      console.error("Error getting media devices", devicesOrFail.error);
      return;
    }

    const devices = devicesOrFail.value!;
    const { videoinput, audioinput } = groupBy(prop("kind"), devices);
    let video = constraints?.video ?? defaultConstraints.video;
    let audio = constraints?.audio ?? defaultConstraints.audio;

    if (videoinput && videoinput.length > 0) {
      const [, bestIndex] = getBestCamera(videoinput);
      const deviceId = videoinput[bestIndex].deviceId;
      if (typeof video == "boolean") {
        video = { deviceId };
      } else {
        video = { ...video, deviceId };
      }
    } else {
      console.error("no cameras found");
    }

    if (audioinput && audioinput.length > 0) {
      const deviceId = audioinput[0].deviceId;
      if (typeof audio == "boolean") {
        audio = { deviceId };
      } else {
        audio = { ...audio, deviceId };
      }
    } else {
      console.error("no microphones found");
    }
    setConstraints({ video, audio });
  }, [devicesOrFail.loading]);

  const devices = devicesOrFail.value || [];

  const microphones = devices.filter((d) => d.kind == "audioinput");
  const cameras = devices.filter((d) => d.kind == "videoinput");

  const stillCheckingPermissions = permissions.length == 0;
  const denied = !stillCheckingPermissions && permissions.some((p) => p == "denied");
  const prompt = !stillCheckingPermissions && permissions.some((p) => p == "prompt");

  function toggleMic() {
    if (stream) {
      stream.getAudioTracks().forEach((t) => (t.enabled = !t.enabled));
      refresh();
    }
  }
  function toggleVideo() {
    if (stream) {
      stream.getVideoTracks().forEach((t) => (t.enabled = !t.enabled));
      refresh();
    }
  }
  const audioMuted = stream?.getAudioTracks().some((t) => !t.enabled) ?? false;
  const videoMuted = stream?.getVideoTracks().some((t) => !t.enabled) ?? false;

  const controlPanel = (
    <div className={style.controlPanel}>
      <Row justifyContent={"center"} gap={"20px"}>
        <VideoButton
          icon={audioMuted ? "microphoneMuted" : "microphone"}
          onClick={toggleMic}
          width="45px"
          height="42px"
        />
        <VideoButton
          icon={videoMuted ? "videocameraMuted" : "videocamera"}
          onClick={toggleVideo}
          width="45px"
          height="42px"
        />
      </Row>
    </div>
  );

  const hasVideo = !!stream && !videoMuted;
  const message =
    stillCheckingPermissions || !stream
      ? "Waiting for video"
      : prompt
      ? "In order for others to see and hear you, allow your browser to use your camera and microphone, you can turn these back off at any time."
      : denied
      ? "In order to be able to join the video chat, allow your browser to use your camera and microphone. This can be done in 'site settings' in your browser, and can be changed at any time."
      : "Set up your video and audio before joining";

  //TODO: make the size expand or shrink and video should follow suit
  return (
    <Modal>
      <AvoidControls>
        <VideoModal>
          <Box padding="25px">
            <Stack>
              <div>
                <Title>Get started</Title>
                <Row gap="2em" justifyContent="space-between">
                  <span className={style.welcomeMsg}>{message}</span>
                  {status == "idle" && devices.length > 0 && (
                    <div
                      role="button"
                      style={{
                        color: settingsPanel ? "#1973FF" : "#113357",
                        fill: settingsPanel ? "#E6F1FF" : "none",
                        cursor: "pointer",
                      }}
                      onClick={() => setSettingsPanel(!settingsPanel)}
                    >
                      <svg className="icon" width="29px" height="29px">
                        <use href={getPathPrefix("/images/svg-defs.svg#gears")}></use>
                      </svg>
                    </div>
                  )}
                </Row>
              </div>
              <div />

              <div
                className={style.videoContainer2}
                style={{
                  backgroundColor: videoMuted ? userInfo?.color : "white",
                }}
              >
                {stream ? (
                  <>
                    <LargeSelfVideo stream={stream} hidden={!hasVideo} />
                    {controlPanel}
                    {!hasVideo && <div className={cn(style.centered, style.personName)}>{userInfo?.name}</div>}
                  </>
                ) : (
                  <Player
                    autoplay
                    loop
                    src={getPathPrefix("/images/loading-v3.json")}
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "100%",
                      height: "100%",
                      background: "white",
                    }}
                  />
                )}
              </div>
              <CenteredText>
                {status == "idle" ? (
                  <div data-testid="join-video-call" data-enabled={!!stream}>
                    <StyledButton
                      enabled={!!stream}
                      title={videoChat.others?.length == 0 ? "Start video chat" : "Join video chat"}
                      onClick={() => onConnectToCall(stream!)}
                    />
                  </div>
                ) : (
                  <div>Connecting...</div>
                )}
              </CenteredText>
            </Stack>
          </Box>
          {settingsPanel && (
            <div
              style={{
                position: "absolute",
                left: "100%",
                top: 0,
                bottom: 0,
                width: "255px",
              }}
            >
              <Box padding="5em 10px" background="#F8FBFF" border="1px solid #EBEDF3" expandHeight={true}>
                <Stack>
                  <div className={style.optionSelect}>
                    <label>Microphone</label>
                    <DropdownPicker
                      options={microphones}
                      isSelected={(opt) => {
                        if (constraints && typeof constraints.audio == "object") {
                          return constraints.audio.deviceId == opt.deviceId;
                        } else {
                          return opt.label.includes("Default");
                        }
                      }}
                      titles={(opt) => opt.label}
                      enabled={microphones.length > 0}
                      onChange={(opt) => {
                        setConstraints((prev) => {
                          return prev
                            ? { video: prev.video, audio: { ...(prev as any).audio, deviceId: opt.deviceId } }
                            : { audio: { deviceId: opt.deviceId } };
                        });
                      }}
                    />
                  </div>
                  <div className={style.optionSelect}>
                    <label>Camera</label>
                    <DropdownPicker
                      options={cameras}
                      isSelected={(opt) => {
                        if (constraints && typeof constraints.video == "object") {
                          return constraints.video.deviceId == opt.deviceId;
                        } else {
                          return opt.label.includes("Default");
                        }
                      }}
                      titles={(opt) => opt.label}
                      enabled={cameras.length > 0}
                      onChange={(opt) => {
                        setConstraints((prev) => {
                          return prev
                            ? { audio: prev.audio, video: { ...(prev as any).video, deviceId: opt.deviceId } }
                            : { video: { deviceId: opt.deviceId } };
                        });
                      }}
                    />
                  </div>
                </Stack>
              </Box>
            </div>
          )}
          <div role="button" className={style.dismissButton} onClick={onDismiss}>
            <CloseIcon color="currentColor" />
          </div>
        </VideoModal>
      </AvoidControls>
    </Modal>
  );
}

function VideoCallViews({
  syncService,
  stream,
  user,
  room,
  onHangup,
}: {
  syncService: SyncService<RW> | null;
  stream: MediaStream;
  user?: BoardUser | null;
  room: VideoRoomHandler;
  onHangup: () => void;
}) {
  const [audioOn, setAudioOn] = useState(() => stream.getAudioTracks().every((t) => t.enabled));
  const [videoOn, setVideoOn] = useState(() => stream.getVideoTracks().every((t) => t.enabled));
  const [remoteStreams, setRemoteStreams] = useState<RemoteStreamData[]>([]);
  const videoChat = useCollaboratorsInVideoChat(syncService?.getReplicache());

  useEffect(() => {
    if (room.status == "connected") {
      room.notifyChange((videoChat.others as any) ?? [], setRemoteStreams);
    }
  }, [videoChat.others, room.status]);

  function toggleAudio() {
    setAudioOn((m) => {
      stream.getAudioTracks().forEach((t) => (t.enabled = !m));
      // notice we set muted state, which is the opposite of the enabled state,
      // and since we're going to flip it we set the opposite value of the current,
      // so the same value.
      syncService?.getReplicache()?.mutate.setVideoPresence({ audioMuted: m });
      return !m;
    });
  }

  function toggleVideo() {
    setVideoOn((m) => {
      stream.getVideoTracks().forEach((t) => (t.enabled = !m));
      // see comment above in toggleAudio
      syncService?.getReplicache()?.mutate.setVideoPresence({ videoMuted: m });
      return !m;
    });
  }

  let micbuttonstyling: CSSProperties | undefined;
  if (!audioOn) micbuttonstyling = { visibility: "visible" };

  return (
    <Draggable
      axis="both"
      bounds="#no-controls-area"
      positionOffset={{ x: 100, y: 60 }}
      defaultPosition={{ x: 100, y: 100 }}
    >
      <div className={style.videoCarousel}>
        <div className={style.videoContainerInCarousel}>
          {!videoOn && user && (
            <div className={style.cover} style={{ backgroundColor: user.color, color: user.textColor }}>
              {user.name}
            </div>
          )}
          <SmallVideo stream={stream} self={true} hidden={!videoOn} />
          <div className={cn(style.controls, style.videoFooter)}>
            <VideoButton
              icon={audioOn ? "microphone" : "microphoneMuted"}
              onClick={toggleAudio}
              height="100%"
              preserveAspectRatio="xMidYMid"
              extraStyle={micbuttonstyling}
            />
            <VideoButton
              icon={videoOn ? "videocamera" : "videocameraMuted"}
              onClick={toggleVideo}
              height="100%"
              preserveAspectRatio="xMidYMid"
            />
            <VideoButton icon={"hangup"} onClick={onHangup} height="100%" preserveAspectRatio="xMidYMid" />
          </div>
        </div>

        {/* ------------------------
            I check that stream is active to avoid getting stale data of disconnected users.
            Ideally we should have a way to remove stale data from the replicache.
        */}
        {remoteStreams.map(
          (streamData) =>
            streamData.stream.active && <SmallVideoView key={streamData.sessionId} streamData={streamData} />
        )}
      </div>
    </Draggable>
  );
}

function SmallVideoView({ streamData }: { streamData: RemoteStreamData }) {
  const { userInfo, stream, videoMuted, audioMuted, ready } = streamData;
  return (
    <div className={style.videoContainerInCarousel}>
      {(videoMuted || !ready) && (
        <div className={style.cover} style={{ backgroundColor: userInfo.color, color: userInfo.textColor }}>
          {userInfo.name}
        </div>
      )}
      <SmallVideo stream={stream} hidden={videoMuted} self={false} />
      <div className={style.videoFooter}>
        {audioMuted && <VideoIcon icon="microphoneMuted" height="100%" preserveAspectRatio="xMidYMid" />}
        {!videoMuted && (
          <span className={style.nameLabel} style={{ backgroundColor: userInfo.color, color: userInfo.textColor }}>
            {userInfo.name}
          </span>
        )}
      </div>
    </div>
  );
}

const VideoModal: React.FC = ({ children }) => {
  return <div className={cn(style.videoModal, style.centered)}>{children}</div>;
};

const LargeSelfVideo: React.FC<{ stream: MediaStream | null; hidden?: boolean }> = ({ stream, hidden = false }) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current && stream) {
      ref.current.srcObject = stream;
      ref.current.muted = true;
    }
  }, [ref.current, stream]);

  return <video hidden={hidden} ref={ref} width="100%" height="auto" autoPlay />;
};

const SmallVideo: React.FC<{ stream: MediaStream | null; hidden?: boolean; self: boolean }> = ({
  stream,
  self,
  hidden = false,
}) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current && stream) {
      ref.current.srcObject = stream;
      ref.current.muted = self;
    }
  }, [ref.current, stream, self]);

  return <video hidden={hidden} ref={ref} width="100%" height="auto" autoPlay />;
};

const Title: React.FC = ({ children }) => {
  return <div className={style.title}>{children}</div>;
};

const CenteredText: React.FC = ({ children }) => {
  return <div className={style.centerText}>{children}</div>;
};

const VideoButton: React.FC<{
  icon: string;
  width?: string;
  height?: string;
  extraStyle?: Omit<CSSProperties, "width" | "height">;
  preserveAspectRatio?: string;
  onClick: () => void;
}> = ({ icon, width, height, preserveAspectRatio, extraStyle, onClick }) => {
  return (
    <div role="button" onClick={onClick} style={{ ...extraStyle, width, height }}>
      <VideoIcon icon={icon} width={width} height={height} preserveAspectRatio={preserveAspectRatio} />
    </div>
  );
};

const VideoIcon: React.FC<{
  icon: string;
  width?: string;
  height?: string;
  preserveAspectRatio?: string;
}> = ({ icon, width, height, preserveAspectRatio }) => {
  return (
    <svg
      className={cn("icon", style.iconVideo)}
      width={width}
      height={height}
      preserveAspectRatio={preserveAspectRatio}
    >
      <use href={`/images/svg-defs.svg#${icon}`}></use>
    </svg>
  );
};
